import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Calendar, ArrowRight, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { blogPosts } from "../data/mockData";
import SectionHeader from "../components/SectionHeader";

const categories = ["All", "Family Guidance", "Safety & Wellness", "Aging in Place", "Specialized Care"];

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = blogPosts.filter((p) => {
    const matchCat = selectedCategory === "All" || p.category === selectedCategory;
    const matchQuery =
      !query ||
      p.title.toLowerCase().includes(query.toLowerCase()) ||
      p.excerpt.toLowerCase().includes(query.toLowerCase());
    return matchCat && matchQuery;
  });

  const featured = filtered[0];
  const others = filtered.slice(1);

  return (
    <div data-testid="blog-page" className="bg-stone-50">
      {/* HERO */}
      <section className="relative bg-amber-50/30 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-up">
            <p className="text-amber-700 text-sm uppercase tracking-[0.3em] font-semibold mb-5">
              Resources &amp; Reflections
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-slate-900 leading-[1.05] tracking-tight">
              Guidance for families{" "}
              <span className="italic text-amber-700">who care.</span>
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl">
              Thoughtful articles to help you navigate senior care with confidence, warmth, and wisdom.
            </p>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="py-10 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  data-testid={`blog-category-${cat.toLowerCase().replace(/\s|&/g, "-")}`}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat
                      ? "bg-slate-900 text-white"
                      : "bg-white text-slate-700 border border-stone-200 hover:border-amber-700"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="relative w-full lg:w-72">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="blog-search-input"
                type="text"
                placeholder="Search articles..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-11 pr-4 py-6 rounded-full border-stone-200 bg-white focus-visible:ring-amber-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ARTICLES */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-serif text-3xl text-slate-900 mb-4">No articles found</p>
              <p className="text-slate-600">Try a different search or category.</p>
            </div>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <Link
                  to={`/blog/${featured.id}`}
                  data-testid={`blog-featured-${featured.id}`}
                  className="group grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-20 lg:mb-28"
                >
                  <div className="aspect-[4/3] rounded-[2rem] overflow-hidden">
                    <img
                      src={featured.image}
                      alt={featured.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div>
                    <Badge className="bg-amber-700 hover:bg-amber-800 text-white rounded-full mb-5 px-4 py-1.5">
                      Featured
                    </Badge>
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                      <span>{featured.category}</span>
                      <span>&middot;</span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        {featured.readTime}
                      </span>
                    </div>
                    <h2 className="font-serif text-4xl lg:text-5xl text-slate-900 leading-tight tracking-tight group-hover:text-amber-700 transition-colors mb-5">
                      {featured.title}
                    </h2>
                    <p className="text-slate-600 text-lg leading-relaxed mb-6">
                      {featured.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-amber-700 font-semibold">
                      Read article
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              )}

              {/* Others */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                {others.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.id}`}
                    data-testid={`blog-card-${post.id}`}
                    className="group block"
                  >
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden mb-5">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                      <span className="font-medium text-amber-700">{post.category}</span>
                      <span>&middot;</span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="font-serif text-2xl text-slate-900 leading-tight group-hover:text-amber-700 transition-colors mb-3">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed line-clamp-3">
                      {post.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
